import { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import { Form, Input, Switch } from "antd";
import { CustomModal } from "../../components/Modals/CustomModal";
import "./InventarioAddNewStore.css";

/**
 * Modal para editar un almacén existente
 *
 * @param {boolean} visible - Controla visibilidad del modal
 * @param {Function} onClose - Callback al cerrar
 * @param {Function} onSubmit - Callback al guardar cambios
 * @param {Object} almacen - Datos del almacén a editar { id, nombre, activo }
 */
export const InventarioEditStore = ({ visible, onClose, onSubmit, almacen, saving = false }) => {
  const [form] = Form.useForm();
  const initialValuesRef = useRef(null);

  /**
   * Poblar el formulario cuando se abre el modal o cambia el almacén
   */
  useEffect(() => {
    if (visible && almacen) {
      const initialValues = {
        storeName: almacen.name || "",
        status: almacen.active ?? true,
      };

      form.setFieldsValue(initialValues);
      initialValuesRef.current = initialValues;
    }
  }, [visible, almacen, form]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      initialValuesRef.current = null;
    }
  }, [visible, form]);

  const hasRealChanges = () => {
    const currentFormValues = form.getFieldsValue();
    const initial = initialValuesRef.current;

    if (!initial) return true;

    return (
      currentFormValues.storeName !== initial.storeName ||
      currentFormValues.status !== initial.status
    );
  };

  const handleOk = () => {
    if (saving) return;

    if (!hasRealChanges()) {
      form.setFields([
        {
          name: "storeName",
          errors: ["Debe modificar al menos un campo antes de guardar."],
        },
      ]);
      return;
    }

    form.submit();
  };

  const handleCancel = () => {
    if (saving) return;
    form.resetFields();
    onClose();
  };

  const onFinish = async (values) => {
    if (!almacen) {
      console.error("❌ No hay almacén para actualizar");
      return;
    }

    await onSubmit({
      id: almacen.id,
      name: values.storeName,
      active: values.status,
    });
  };

  if (!almacen && visible) {
    return null;
  }

  return (
    <CustomModal
      visible={visible}
      title="Editar almacén"
      onClose={handleCancel}
      onOk={handleOk}
      okText={saving ? "Guardando..." : "Guardar"}
      cancelText="Cancelar"
      width="400px"
      okButtonProps={{
        loading: saving,
        disabled: saving,
      }}
      cancelButtonProps={{
        disabled: saving,
      }}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        onFinish={onFinish}
        className="add-store-form"
      >
        <div className="add-store-form-row">
          <Form.Item
            name="storeName"
            label="Indique el nombre del Almacén"
            rules={[
              { required: true, message: "Ingrese nombre del almacén" },
              { min: 3, message: "Mínimo 3 caracteres" },
              { max: 50, message: "Máximo 50 caracteres" },
            ]}
            className="add-store-input-item"
          >
            <Input placeholder="Almacén" className="add-store-input" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Activo"
            valuePropName="checked"
            className="add-store-switch-item"
          >
            <Switch />
          </Form.Item>
        </div>
      </Form>
    </CustomModal>
  );
};

InventarioEditStore.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  almacen: PropTypes.object,
  saving: PropTypes.bool,
};
